import { Link } from 'react-router-dom'
import {ArrowRight, CheckCircle, TrendingUp, Shield, Users} from 'lucide-react'

function Home() {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-orange-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div>
              <div className="inline-flex items-center space-x-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <TrendingUp className="w-4 h-4" />
                <span>Trusted by 50,000+ Users</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                Discover Your Perfect{' '}
                <span className="text-teal-600">Vitamin</span>{' '}
                <span className="text-orange-500">Protocol</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Calculate your exact vitamin needs with our Vitamin Vitality Tool. Get personalized recommendations based on your age, lifestyle, and health goals.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/calculator"
                  className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 text-white rounded-lg text-lg font-semibold hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl"
                >
                  Take Free Vitality Quiz
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                
                <Link
                  to="/reviews"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-gray-900 rounded-lg text-lg font-semibold border-2 border-gray-200 hover:border-teal-500 transition-all"
                >
                  Browse Reviews
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center space-x-6 mt-8">
                <div className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-teal-600" />
                  <span className="text-sm text-gray-600">Science-Backed</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Users className="w-5 h-5 text-teal-600" />
                  <span className="text-sm text-gray-600">Expert Reviews</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-5 h-5 text-teal-600" />
                  <span className="text-sm text-gray-600">Unbiased Recommendations</span>
                </div>
              </div>
            </div>

            {/* Right Content - Calculator Preview */}
            <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-100 rounded-full mb-4">
                  <TrendingUp className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Quick Vitality Check</h3>
                <p className="text-gray-600">Answer 5 questions, get personalized results</p>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Energy Level</span>
                    <span className="text-xs text-teal-600 font-semibold">5/10</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-teal-500 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Common Concerns</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">Fatigue</span>
                    <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium">Immunity</span>
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Hair Health</span>
                  </div>
                </div>

                <Link
                  to="/calculator"
                  className="block w-full py-3 bg-teal-600 text-white text-center rounded-lg font-semibold hover:bg-teal-700 transition-colors"
                >
                  Start Full Assessment
                </Link>
              </div>

              <p className="text-xs text-gray-500 text-center mt-4">
                ⏱️ Takes only 2 minutes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Top Vital Supplements</h2>
          <p className="text-lg text-gray-600">Expert-reviewed products for optimal vitality</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Product Card 1 */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-teal-500 transition-all hover:shadow-lg">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg h-48 mb-6 flex items-center justify-center">
              <div className="text-6xl">💊</div>
            </div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">B Vital Total</h3>
              <div className="flex items-center space-x-1">
                <span className="text-yellow-500">★</span>
                <span className="text-sm font-semibold text-gray-900">4.8</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">Complete B-Complex for energy and focus</p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                <span>Boosts energy levels</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                <span>Supports metabolism</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                <span>Reduces fatigue</span>
              </li>
            </ul>
            <a
              href="#"
              className="block w-full py-3 bg-orange-500 text-white text-center rounded-lg font-semibold hover:bg-orange-600 transition-colors"
            >
              Check Best Price
            </a>
          </div>

          {/* Product Card 2 */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-teal-500 transition-all hover:shadow-lg">
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg h-48 mb-6 flex items-center justify-center">
              <div className="text-6xl">☀️</div>
            </div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">D Vital 10,000</h3>
              <div className="flex items-center space-x-1">
                <span className="text-yellow-500">★</span>
                <span className="text-sm font-semibold text-gray-900">4.9</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">High-potency Vitamin D for immunity</p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                <span>Strengthens immune system</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                <span>Supports bone health</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                <span>Improves mood</span>
              </li>
            </ul>
            <a
              href="#"
              className="block w-full py-3 bg-orange-500 text-white text-center rounded-lg font-semibold hover:bg-orange-600 transition-colors"
            >
              Check Best Price
            </a>
          </div>

          {/* Product Card 3 */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 hover:border-teal-500 transition-all hover:shadow-lg">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg h-48 mb-6 flex items-center justify-center">
              <div className="text-6xl">🛡️</div>
            </div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xl font-bold text-gray-900">Immunovit Plus</h3>
              <div className="flex items-center space-x-1">
                <span className="text-yellow-500">★</span>
                <span className="text-sm font-semibold text-gray-900">4.7</span>
              </div>
            </div>
            <p className="text-sm text-gray-600 mb-4">Advanced immune support formula</p>
            <ul className="space-y-2 mb-6">
              <li className="flex items-start space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                <span>Zinc + Vitamin C</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                <span>Antioxidant protection</span>
              </li>
              <li className="flex items-start space-x-2 text-sm text-gray-700">
                <CheckCircle className="w-4 h-4 text-teal-600 mt-0.5 flex-shrink-0" />
                <span>Daily defense formula</span>
              </li>
            </ul>
            <a
              href="#"
              className="block w-full py-3 bg-orange-500 text-white text-center rounded-lg font-semibold hover:bg-orange-600 transition-colors"
            >
              Check Best Price
            </a>
          </div>
        </div>

        <div className="text-center mt-12">
          <Link
            to="/reviews"
            className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-700 transition-colors"
          >
            View All Reviews
            <ArrowRight className="w-5 h-5 ml-2" />
          </Link>
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600">Get personalized vitamin recommendations in 3 simple steps</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500 text-white rounded-full text-2xl font-bold mb-4">
                1
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Answer Questions</h3>
              <p className="text-gray-600">Tell us about your age, lifestyle, and health concerns</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500 text-white rounded-full text-2xl font-bold mb-4">
                2
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Get Your Score</h3>
              <p className="text-gray-600">Receive your personalized Vitality Score and analysis</p>
            </div>

            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-teal-500 text-white rounded-full text-2xl font-bold mb-4">
                3
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Start Your Protocol</h3>
              <p className="text-gray-600">Follow expert recommendations tailored to your needs</p>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link
              to="/calculator"
              className="inline-flex items-center px-8 py-4 bg-orange-500 text-white rounded-lg text-lg font-semibold hover:bg-orange-600 transition-all shadow-lg"
            >
              Start Your Vitality Journey
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Home
